import { useCallback, useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase/client'

const METHOD_COLUMNS = [
  ['ET', 'et'],
  ['MPI', 'mt'],
  ['PT', 'pt'],
  ['VT', 'vt'],
  ['UT', 'ut'],
  ['RT', 'rt'],
  ['LT', 'lt'],
  ['Thread', 'thread'],
] as const

const PROFILE_COLUMNS = [
  'role',
  'supervisor',
  ...METHOD_COLUMNS.flatMap(([, col]) => [col, `${col}_level`]),
].join(', ')

type NdtAccess = 'all' | Set<string>

const loadAccess = async (): Promise<NdtAccess> => {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return 'all'

  const { data, error } = await supabase
    .from('profiles')
    .select(PROFILE_COLUMNS)
    .eq('id', user.id)
    .single()

  if (error || !data) {
    console.error('Error loading NDT qualifications:', error)
    return new Set()
  }

  const profile = data as unknown as Record<string, unknown>
  if (profile.role === 'admin' || profile.supervisor === true) return 'all'

  // An explicit `false` flag overrides any level; otherwise any level above 0 qualifies.
  const qualified = new Set<string>()
  for (const [method, col] of METHOD_COLUMNS) {
    const flag = profile[col]
    const level = profile[`${col}_level`]
    if (flag === true || (flag !== false && typeof level === 'number' && level > 0)) {
      qualified.add(method)
    }
  }
  return qualified
}

export function useNdtQualifications() {
  const [access, setAccess] = useState<NdtAccess | null>(null)

  useEffect(() => {
    let cancelled = false
    loadAccess().then((result) => {
      if (!cancelled) setAccess(result)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const isQualified = useCallback(
    (method: string) => access === 'all' || (access?.has(method) ?? false),
    [access],
  )

  return { loading: access === null, isQualified }
}
